import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Title, 
  Text, 
  Table, 
  TableHead, 
  TableRow, 
  TableHeaderCell, 
  TableBody, 
  TableCell,
  Badge,
  Flex,
  Select,
  SelectItem,
  DateRangePicker,
  Button,
  Metric
} from '@tremor/react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { request } from '../../../services/request';

export default function AdminUsage() {
  const [topDevices, setTopDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: new Date()
  });
  const [limit, setLimit] = useState('20');

  const fetchTopUsage = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit
      });
      
      if (dateRange.from) {
        params.append('startDate', dateRange.from.toISOString());
      }
      if (dateRange.to) {
        params.append('endDate', dateRange.to.toISOString());
      }

      const data = await request(`/api/users/admin/top-usage?${params}`);
      setTopDevices(data.data || []);
    } catch (error) {
      console.error('Error fetching top usage:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTopUsage();
  }, []);

  const handleDateRangeChange = (value) => {
    setDateRange(value);
  };

  const handleRefresh = () => {
    fetchTopUsage();
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const getDeviceTypeBadge = (type) => {
    const colors = {
      mqtt: 'blue',
      lora: 'green',
      http: 'yellow'
    };
    return <Badge color={colors[type] || 'gray'}>{type?.toUpperCase() || 'Unknown'}</Badge>;
  };

  return (
    <>
      <Card className="mb-6">
        <Flex justifyContent="between" alignItems="center" className="mb-4">
          <Title>Top Device Usage</Title>
          <Flex className="space-x-2" justifyContent="end">
            <DateRangePicker
              value={dateRange}
              onValueChange={handleDateRangeChange}
              enableYearNavigation
              placeholder="Select date range"
              className="max-w-sm"
            />
            <Select value={limit} onValueChange={setLimit} className="w-32">
              <SelectItem value="10">Top 10</SelectItem>
              <SelectItem value="20">Top 20</SelectItem>
              <SelectItem value="50">Top 50</SelectItem>
              <SelectItem value="100">Top 100</SelectItem>
            </Select>
            <Button 
              icon={ArrowPathIcon} 
              onClick={handleRefresh}
              loading={loading}
            >
              Refresh
            </Button>
          </Flex>
        </Flex>
        
        <Text className="mb-6">
          Showing devices with the highest data consumption for the selected period
        </Text>

        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Rank</TableHeaderCell>
              <TableHeaderCell>Device Name</TableHeaderCell>
              <TableHeaderCell>Serial</TableHeaderCell>
              <TableHeaderCell>Type</TableHeaderCell>
              <TableHeaderCell>Total Events</TableHeaderCell>
              <TableHeaderCell>Owner Email</TableHeaderCell>
              <TableHeaderCell>Owner Name</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">
                  <Text>Loading...</Text>
                </TableCell>
              </TableRow>
            ) : topDevices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center">
                  <Text>No usage data found for the selected period</Text>
                </TableCell>
              </TableRow>
            ) : (
              topDevices.map((device, index) => (
                <TableRow key={device.deviceId}>
                  <TableCell>
                    <Text className="font-medium">{index + 1}</Text>
                  </TableCell>
                  <TableCell>
                    <Link 
                      to={`/devices/${device.serial}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {device.deviceName || device.serial}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Text>{device.serial}</Text>
                  </TableCell>
                  <TableCell>
                    {getDeviceTypeBadge(device.deviceType)}
                  </TableCell>
                  <TableCell>
                    <Metric>{formatNumber(device.totalEvents)}</Metric>
                  </TableCell>
                  <TableCell>
                    <Link 
                      to={`mailto:${device.ownerEmail}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {device.ownerEmail}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Text>{device.ownerName || '-'}</Text>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {!loading && topDevices.length > 0 && (
        <Card>
          <Title>Summary Statistics</Title>
          <Flex className="mt-4 space-x-6">
            <div>
              <Text>Total Events</Text>
              <Metric>
                {formatNumber(topDevices.reduce((sum, device) => sum + device.totalEvents, 0))}
              </Metric>
            </div>
            <div>
              <Text>Average Events per Device</Text>
              <Metric>
                {formatNumber(Math.round(topDevices.reduce((sum, device) => sum + device.totalEvents, 0) / topDevices.length))}
              </Metric>
            </div>
            <div>
              <Text>Unique Owners</Text>
              <Metric>
                {new Set(topDevices.map(d => d.ownerEmail)).size}
              </Metric>
            </div>
          </Flex>
        </Card>
      )}
    </>
  );
}